import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { adminApi } from '../services/adminApi';

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
    const [admin, setAdmin] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);

    // Verifier l'authentification au chargement
    const checkAuth = useCallback(async () => {
        const token = localStorage.getItem('admin_access_token');
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const response = await adminApi.getMe();
            if (response?.data?.admin) {
                setAdmin(response.data.admin);
                setIsAuthenticated(true);
            }
        } catch (error) {
            console.error('Admin auth check failed:', error);
            localStorage.removeItem('admin_access_token');
            localStorage.removeItem('admin_refresh_token');
            setAdmin(null);
            setIsAuthenticated(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    // Connexion
    const login = async (email, password) => {
        try {
            const response = await adminApi.login(email, password);
            if (response?.data?.access_token) {
                localStorage.setItem('admin_access_token', response.data.access_token);
                if (response.data.refresh_token) {
                    localStorage.setItem('admin_refresh_token', response.data.refresh_token);
                }
                setAdmin(response.data.admin);
                setIsAuthenticated(true);
                return { success: true };
            }
            return { success: false, error: 'Reponse invalide' };
        } catch (error) {
            const message = error.response?.data?.error || error.message || 'Erreur de connexion';
            return { success: false, error: message };
        }
    };

    // Deconnexion
    const logout = async () => {
        try {
            const refreshToken = localStorage.getItem('admin_refresh_token');
            await adminApi.logout(refreshToken);
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem('admin_access_token');
            localStorage.removeItem('admin_refresh_token');
            setAdmin(null);
            setIsAuthenticated(false);
        }
    };

    // Verifier si super admin
    const isSuperAdmin = () => {
        return admin?.role === 'super_admin';
    };

    const value = {
        admin,
        isAuthenticated,
        loading,
        login,
        logout,
        isSuperAdmin,
        checkAuth
    };

    return (
        <AdminAuthContext.Provider value={value}>
            {children}
        </AdminAuthContext.Provider>
    );
}

export function useAdminAuth() {
    const context = useContext(AdminAuthContext);
    if (!context) {
        throw new Error('useAdminAuth must be used within AdminAuthProvider');
    }
    return context;
}

export default AdminAuthContext;
